// AdminYabbitWebhookPage.js — diagnostic for confirming Yabbit is
// actually POSTing to /api/yabbit/webhook, with config instructions if
// it isn't. Master-admin only.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import {
  Webhook, RefreshCw, Loader2, CheckCircle2, XCircle, AlertCircle, Copy,
  ChevronDown, ChevronUp, PhoneIncoming, PhoneOutgoing,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const HealthPill = ({ health }) => {
  const map = {
    healthy:        { label: 'Receiving',       cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40', Icon: CheckCircle2 },
    stale:          { label: 'Stale (>24h)',    cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40',       Icon: AlertCircle },
    not_receiving:  { label: 'Not receiving',   cls: 'bg-red-500/20 text-red-300 border-red-500/40',             Icon: XCircle },
  };
  const meta = map[health] || map.not_receiving;
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${meta.cls}`} data-testid={`yabbit-health-${health}`}>
      <Icon className="w-3.5 h-3.5" />{meta.label}
    </span>
  );
};

const AdminYabbitWebhookPage = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showRecent, setShowRecent] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await axios.get(`${API}/admin/yabbit/webhook-status`, { headers });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load webhook status.');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const copyUrl = () => {
    if (!data?.config?.webhook_url) return;
    navigator.clipboard.writeText(data.config.webhook_url).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto" data-testid="admin-yabbit-webhook-page">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Webhook className="w-6 h-6 text-teal-400" />
              Yabbit Webhook Health
            </h1>
            <p className="text-sm text-white/60 mt-1">
              Verifies whether Yabbit is actually POSTing ringing events into this platform. If not, follow the config below.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 disabled:opacity-50"
            data-testid="yabbit-webhook-reload"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Reload
          </button>
        </div>

        {err && (
          <div className="mb-4 flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-200">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />{err}
          </div>
        )}

        {data && (
          <>
            {/* Health summary */}
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 mb-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-white/50 text-[10px] uppercase tracking-widest mb-1">Status</div>
                  <HealthPill health={data.health} />
                </div>
                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-3xl font-bold text-white" data-testid="events-24h">{data.events_last_24h}</div>
                    <div className="text-[11px] text-white/50 uppercase tracking-wider">Last 24h</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-white">{data.events_last_7d}</div>
                    <div className="text-[11px] text-white/50 uppercase tracking-wider">Last 7d</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-white">{data.total_events_ever}</div>
                    <div className="text-[11px] text-white/50 uppercase tracking-wider">All time</div>
                  </div>
                </div>
              </div>
              {Object.keys(data.events_by_state_7d || {}).length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="text-white/50 text-[10px] uppercase tracking-widest mb-2">Events by state (7d)</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(data.events_by_state_7d).map(([state, n]) => (
                      <span key={state} className="bg-white/5 border border-white/10 text-white/80 px-2.5 py-1 rounded text-xs">
                        <span className="font-mono">{state}</span>: <span className="font-bold text-teal-300">{n}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Config instructions — always shown, but banner if not_receiving */}
            <div className="bg-teal-500/5 border border-teal-500/30 rounded-2xl p-5 mb-4" data-testid="yabbit-webhook-config">
              <h3 className="text-white font-semibold flex items-center gap-2 mb-3">
                <AlertCircle className="w-4 h-4 text-teal-400" />
                Configuration for the Yabbit admin portal
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <div className="text-white/50 text-[10px] uppercase tracking-widest mb-1">Webhook URL (paste this into Yabbit)</div>
                  <div className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-lg px-3 py-2">
                    <code className="text-teal-300 text-xs break-all flex-1">{data.config.webhook_url}</code>
                    <button
                      onClick={copyUrl}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 rounded"
                      data-testid="yabbit-copy-url"
                    >
                      {copied ? <CheckCircle2 className="w-3 h-3 text-teal-300" /> : <Copy className="w-3 h-3" />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
                <div>
                  <div className="text-white/50 text-[10px] uppercase tracking-widest mb-1">Auth header</div>
                  <code className="text-teal-300 text-xs bg-black/30 border border-white/10 rounded px-2 py-0.5">{data.config.auth_method}</code>
                  <span className="text-white/60 text-xs ml-2">— set the value from your platform&apos;s <code className="text-teal-300">backend/.env</code> → <code className="text-teal-300">YABBIT_WEBHOOK_SECRET</code></span>
                </div>
                <div>
                  <div className="text-white/50 text-[10px] uppercase tracking-widest mb-1">Required fields in the JSON body</div>
                  <ul className="text-xs text-white/70 space-y-0.5 pl-4 list-disc">
                    {data.config.required_fields.map((f) => (
                      <li key={f}><code className="text-teal-300">{f.split(' ')[0]}</code>{f.substring(f.indexOf(' '))}</li>
                    ))}
                  </ul>
                </div>
                <div className="text-white/70 text-xs bg-black/20 border border-white/10 rounded-lg p-3 leading-relaxed">
                  {data.config.instructions}
                </div>
                <div className="text-[11px] text-white/50">
                  Trigger events to enable: <code className="text-teal-300">ringing</code>, <code className="text-teal-300">answered</code>, <code className="text-teal-300">terminated</code>.
                  Ringing is the one that fires the caller-ID popup with GymSales / Smokeball lookups.
                </div>
              </div>
            </div>

            {/* Recent events */}
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
              <button
                onClick={() => setShowRecent(!showRecent)}
                className="w-full flex items-center justify-between text-left"
              >
                <h3 className="text-white font-semibold">Recent events ({data.recent_events?.length || 0})</h3>
                {showRecent ? <ChevronUp className="w-4 h-4 text-white/50" /> : <ChevronDown className="w-4 h-4 text-white/50" />}
              </button>
              {showRecent && (
                <div className="mt-3 space-y-1.5" data-testid="yabbit-recent-events">
                  {(data.recent_events || []).length === 0 ? (
                    <div className="text-white/40 text-sm italic py-4 text-center">No events received yet.</div>
                  ) : (
                    data.recent_events.map((e, i) => (
                      <div key={i} className="flex items-center gap-3 bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-xs">
                        <div className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                          {e.direction === 'outbound'
                            ? <PhoneOutgoing className="w-3 h-3 text-teal-300" />
                            : <PhoneIncoming className="w-3 h-3 text-blue-300" />}
                        </div>
                        <div className="min-w-0 flex-1 grid grid-cols-2 md:grid-cols-4 gap-2">
                          <span className="text-white/70 font-mono text-[11px]">{e.state}</span>
                          <span className="text-white/60">{e.from_number || '—'} → {e.to_number || '—'}</span>
                          <span className="text-white/50">ext {e.extension || '—'}</span>
                          <span className="text-white/40">{_fmtDateTime(e.received_at)}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminYabbitWebhookPage;
